import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const QuoteTierVsProposify = () => {
  useEffect(() => {
    analytics.track('Comparison Page View', { comparison: 'QuoteTier vs Proposify' });
  }, []);

  return (
    <SEOPageLayout
      title="QuoteTier vs Proposify Alternative 2025 | Compare Features & Pricing"
      description="Looking for a Proposify alternative? Compare QuoteTier vs Proposify features, pricing, and ease of use. See why businesses choose QuoteTier for interactive quotes."
      canonical="/compare/quotetier-vs-proposify"
      ogTitle="QuoteTier vs Proposify - Which is Right for You?"
      ogDescription="Compare QuoteTier and Proposify side-by-side. QuoteTier offers interactive pricing tiers and automatic upselling that Proposify lacks."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              QuoteTier vs. <span className="text-primary font-bold">Proposify</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Looking for a Proposify alternative? See how QuoteTier compares with interactive pricing that helps customers upsell themselves.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">Feature</th>
                  <th className="text-center py-6 px-6 bg-black text-white">
                    <span className="font-instrument text-lg font-bold">QuoteTier</span>
                  </th>
                  <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">Proposify</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Starting Price</td>
                  <td className="py-4 px-6 text-center font-jetbrains font-bold text-green-600">$0/mo (Free tier)</td>
                  <td className="py-4 px-6 text-center font-jetbrains">$29/user/mo</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Interactive Good/Better/Best Pricing</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Automatic Upselling</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">E-Signatures</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Mobile-Optimized Quotes</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Quote Tracking</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Setup Time</td>
                  <td className="py-4 px-6 text-center font-jetbrains text-green-600 font-bold">5 minutes</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Hours to days</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Learning Curve</td>
                  <td className="py-4 px-6 text-center font-jetbrains text-green-600 font-bold">Simple</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Steep</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-jetbrains font-medium">Free Plan</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Why Choose QuoteTier Over Proposify?</h2>
          <div className="space-y-6 font-jetbrains text-lg text-gray-700">
            <p>
              <strong>Proposify</strong> is a full-featured proposal software designed for enterprise sales teams. It's powerful but complex, with a steep learning curve and pricing that starts at $29 per user per month.
            </p>
            <p>
              <strong>QuoteTier</strong> is built specifically for service businesses who want to increase their average deal size without the complexity. Our unique Good/Better/Best pricing system lets customers explore options and naturally upsell themselves.
            </p>
            <h3 className="font-instrument text-2xl font-bold mt-8 mb-4">Key Differences:</h3>
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>Interactive Pricing Tiers:</strong> QuoteTier's core feature is interactive Good/Better/Best pricing that encourages customers to choose higher-value options. Proposify doesn't offer this.</li>
              <li><strong>Simpler Setup:</strong> Create your first quote in 5 minutes. Proposify requires hours of setup and template customization.</li>
              <li><strong>Better Pricing:</strong> QuoteTier has a free tier. Proposify starts at $29/user/month with no free option.</li>
              <li><strong>Built for Service Businesses:</strong> QuoteTier is designed for contractors, freelancers, and service providers. Proposify is geared toward enterprise sales teams.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Ready to Try a Better Alternative?</h2>
          <p className="font-jetbrains text-xl text-gray-300 mb-8">
            Start free and see why businesses are switching from Proposify to QuoteTier.
          </p>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default QuoteTierVsProposify;
